// @flow

import React from 'react';
import { withTranslatedRouter } from 'shared_data/providers/url/withTranslatedRouter';
import { HTML_ID_APPLICATION_MSG_TOP_LIST, ScrollToTopOnPathUpdate } from '@riseart/common';
import { HeaderContainer } from '@riseart/layout';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { Header } from 'shared_components/layouts/header/Header';
import { Footer } from 'shared_components/layouts/Footer';
import { PagePreloader } from 'shared_data/components/PagePreloader';
import { RouteConfigContext } from 'shared_data/providers/routeconfig/Context';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { CmsViewLoader } from 'shared_components/cms/views/Loader';
import { meta as META_ENUM } from 'Enum';
import { WithOffsetHeight } from 'shared_hocs/gui/withOffsetHeight';
import { CartSidebar } from 'shared_components/cart/Sidebar';
import { RegionalFormOverlay } from 'shared_components/forms/regional/Overlay';
import { RecentArtworksWidget } from 'shared_components/arts/widgets/RecentArtworks';
import { LensOverlay } from 'shared_components/forms/search/lens/Overlay';
import {
  HOCMessagesNotificationsManager,
  NotificationMessages,
} from 'shared_hocs/messages/notifications/Manager';
import {
  HOCMessagesApplicationManager,
  ApplicationMessages,
} from 'shared_hocs/messages/application/Manager';

import { layoutContentCls } from 'shared_components/layouts/Default.less';
import { appMessagesBottomCls } from 'shared_components/messages/styles.less';

export const COLOR_THEMES = {
  WHITE: 'white',
  TRANSPARENT: 'transparent',
};

type Props = {
  className?: string,
  hasNotification?: boolean,
  hasApplicationMessages?: boolean,
  hasHeader?: boolean,
  hasFooter?: boolean,
  hasPagePreloader?: boolean,
  showRecentArtworkWidget?: boolean,
  subscribe?: Array<Object>,
  notify?: Function,
  headerComponent?: ?React$Element<any>,
  layoutQueryErrorHandler?: Object,
  theme?: string,
  scrollTopOnPathChange?: boolean,
  children: any,
  transparent?: boolean,
};

// HOCs
const HOCScrollToTop = withTranslatedRouter(ScrollToTopOnPathUpdate);
const NotificationMessagesContainer = HOCMessagesNotificationsManager(NotificationMessages);
const ApplicationMessagesContainer = HOCMessagesApplicationManager(ApplicationMessages);

export const LayoutDefault = ({
  className,
  hasNotification = true,
  hasApplicationMessages = true,
  hasPagePreloader = true,
  showRecentArtworkWidget = true,
  hasHeader = true,
  hasFooter = true,
  headerComponent: CustomHeader,
  layoutQueryErrorHandler = {},
  scrollTopOnPathChange = false,
  children,
  transparent = false,
}: Props) => {
  const wrapperProps = className ? { className } : null;

  return (
    <div {...wrapperProps}>
      {scrollTopOnPathChange && <HOCScrollToTop />}
      {/*
        LD+JSON data is not using any meta subscriber,
        so we are using another MetaProvided specificly for it
        and we do not include it in the PAGE_META subscriber used above
      */}
      <MetaProvider
        meta={{
          [META_ENUM.METATYPE.SCRIPT_LD_JSON_WEBPAGE]: {
            url: LocationManager.get('fullPath'),
          },
        }}
      />
      <CmsViewLoader layoutQueryErrorHandler={layoutQueryErrorHandler} />
      {hasPagePreloader ? <PagePreloader /> : null}
      {hasNotification ? <NotificationMessagesContainer /> : null}
      <WithOffsetHeight>
        {({ notificationsHeight }) => (
          <HeaderContainer notificationHeight={notificationsHeight}>
            {hasApplicationMessages ? (
              <ApplicationMessagesContainer
                htmlId={HTML_ID_APPLICATION_MSG_TOP_LIST}
                messageLocation="top"
              />
            ) : null}
            {hasHeader
              ? CustomHeader || (
                  <RouteConfigContext.Consumer>
                    {({ ui }) => (
                      <Header
                        transparent={transparent}
                        showRecentWidgetOnPage={showRecentArtworkWidget}
                        disableSticky={!!(ui && ui.disableStickyHeader)}
                      />
                    )}
                  </RouteConfigContext.Consumer>
                )
              : null}
          </HeaderContainer>
        )}
      </WithOffsetHeight>
      <div className={layoutContentCls}>{children}</div>
      {hasFooter ? <Footer /> : null}
      <LensOverlay />
      <CartSidebar />
      <RegionalFormOverlay />
      <RecentArtworksWidget showOnPage={showRecentArtworkWidget} />
      {hasApplicationMessages ? (
        <div className={appMessagesBottomCls}>
          <ApplicationMessagesContainer messageLocation="bottom" />
        </div>
      ) : null}
    </div>
  );
};
